import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import CustomInput from '../../components/CustomInput';
import CustomModal from '../../components/CustomModal';
import CustomTable from '../../components/CustomTable';
import Title from '../../components/Title';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    allThemes,
    themesLoadingStatus,
    getThemes,
    createTheme,
    editTheme,
    deleteTheme,
} from '../../redux/slices/themeSlice';

export default function ThemesPage() {
    const dispatch = useDispatch();

    const themes = useSelector(allThemes);
    const themesStatus = useSelector(themesLoadingStatus);

    const [data, setData] = useState({ name: '', code: '' });
    const [themeId, setThemeId] = useState(null);
    const [isAddThemeModalVisible, setIsAddThemeModalVisible] = useState(false);
    const [isEditThemeModalVisible, setIsEditThemeModalVisible] =
        useState(false);
    const [isDeleteThemeModalVisible, setIsDeleteThemeModalVisible] =
        useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getThemes());
    }, [dispatch]);

    const toggleAddThemeModal = () => {
        resetState();
        setErrors({});
        setIsAddThemeModalVisible(!isAddThemeModalVisible);
    };

    const toggleEditThemeModal = (record) => {
        if (!record) {
            setIsEditThemeModalVisible(!isEditThemeModalVisible);
        } else {
            setData({ name: record.name, code: record.code });
            setThemeId(record.id);
            setErrors({});
            setIsEditThemeModalVisible(!isEditThemeModalVisible);
        }
    };

    const toggleDeleteThemeModal = (record) => {
        if (!record) {
            setIsDeleteThemeModalVisible(!isDeleteThemeModalVisible);
        } else {
            setData({ name: record.name, code: record.code });
            setThemeId(record.id);
            setIsDeleteThemeModalVisible(!isDeleteThemeModalVisible);
        }
    };

    const resetState = () => {
        setData({ name: '', code: '' });
    };

    const onInputChange = (e) => {
        const { id, value } = e.target;

        setData({ ...data, [id]: value });
    };

    const onAddTheme = async () => {
        try {
            await dispatch(createTheme(data)).unwrap();
            toast.success('New theme successfully added');
            toggleAddThemeModal();
            dispatch(getThemes());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditTheme = async () => {
        const requestData = {
            id: themeId,
            body: data,
        };

        try {
            await dispatch(editTheme(requestData)).unwrap();
            toast.success('Changes saved successfully');
            toggleEditThemeModal();
            dispatch(getThemes());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteTheme = async () => {
        try {
            await dispatch(deleteTheme(themeId)).unwrap();
            toast.success('Theme deleted successfully');
            toggleDeleteThemeModal();
            dispatch(getThemes());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined
                    onClick={(e) => toggleEditThemeModal(record, e)}
                />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteThemeModal(record, e)}
                />
            </Space>
        ),
    };

    const getFilteredThemes = (data, filter) =>
        data?.filter((el) =>
            el?.name.toLowerCase().includes(filter.toLowerCase()),
        );
    return (
        <div>
            <Helmet>
                <title>Console Defentry - Themes</title>
            </Helmet>
            <Title>Themes</Title>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['ID', 'Name', 'Code', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleAddThemeModal}
                    tablePannelTitle="Table of themes"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Add theme"
                    data={themes}
                    getFilteredData={getFilteredThemes}
                    isLoading={themesStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Add new theme"
                subText="Please enter theme details"
                subTextAlign="left"
                isModalVisible={isAddThemeModalVisible}
                onAcceptButtonClick={onAddTheme}
                onCancelButtonClick={toggleAddThemeModal}
                cancelButtonText="Cancel"
                acceptButtonText="Add"
            >
                <CustomInput
                    id="name"
                    placeholder="Enter theme name"
                    onInputChange={onInputChange}
                    inputValue={data.name}
                    labelText="Name"
                    width="100%"
                    errorText={errors?.name}
                />
                <div style={{ marginTop: '16px' }} />
                <CustomInput
                    id="code"
                    placeholder="Enter theme code"
                    onInputChange={onInputChange}
                    inputValue={data.code}
                    labelText="Theme code"
                    width="100%"
                    errorText={errors?.code}
                />
            </CustomModal>
            <CustomModal
                title="Edit theme"
                subText="Edit theme name below"
                subTextAlign="left"
                isModalVisible={isEditThemeModalVisible}
                onAcceptButtonClick={onEditTheme}
                onCancelButtonClick={toggleEditThemeModal}
                cancelButtonText="Cancel"
                acceptButtonText="Save"
            >
                <CustomInput
                    id="name"
                    placeholder="Enter theme name"
                    onInputChange={onInputChange}
                    inputValue={data.name}
                    labelText="Name"
                    width="100%"
                    errorText={errors?.name}
                />
                <div style={{ marginTop: '16px' }} />
                <CustomInput
                    id="code"
                    placeholder="Enter theme code"
                    onInputChange={onInputChange}
                    inputValue={data.code}
                    labelText="Theme code"
                    width="100%"
                    errorText={errors?.code}
                />
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${data.name} `}</span>
                        theme?
                    </p>
                }
                subText="You will delete this theme without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteThemeModalVisible}
                onAcceptButtonClick={onDeleteTheme}
                onCancelButtonClick={toggleDeleteThemeModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            ></CustomModal>
        </div>
    );
}
